import { Icon, ICONS } from 'components/Icon';
import { Pressable } from 'components/Pressable';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import * as React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { doShowSyncModal } from 'store/app/actions';
import { selectUserId } from 'store/auth/selectors';
import {
  selectHasAssetErrors,
  selectIsNotAuthorized,
} from 'store/error/selectors';
import { selectHasItemsToSync, selectSyncStatus } from 'store/sync/selectors';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { SyncSteps } from 'store/sync/types';
import Animated, {
  cancelAnimation,
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { selectIsOffline } from 'store/app/selectors';
import { Glass } from 'components/Glass';
import { DevIndicator } from './DevIndicator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const CIRCLE_SIZE = 68;
const ICON_SIZE = CIRCLE_SIZE / 2;
const ABSOLUTE_RIGHT_POSITION = 18;

type Props = {
  showSyncIcon: boolean;
};

export const SyncIndicator: React.FC<Props> = ({ showSyncIcon }) => {
  const dispatch = useDispatch();
  const { bottom, right } = useSafeAreaInsets();
  const syncingRef = useRef({
    is: false,
    was: false,
  });

  const toggleSyncDialog = useCallback(() => {
    dispatch(doShowSyncModal({ downloadCleanData: false }));
  }, [dispatch]);

  const hasItemsToSync = useSelector(selectHasItemsToSync);
  const hasUserId = !!useSelector(selectUserId);
  const syncingStatus = useSelector(selectSyncStatus);
  const notAuthorized = useSelector(selectIsNotAuthorized);
  const isOfflineState = useSelector(selectIsOffline);
  const hasAssetErrors = useSelector(selectHasAssetErrors);

  const isAndroid = Platform.OS === 'android';

  if (syncingStatus.step !== SyncSteps.waiting) {
    syncingRef.current.was = true;
  }
  syncingRef.current.is = syncingStatus.step !== SyncSteps.waiting;

  // Just override this constant to see the different states (icon, colour)
  const typeForSyncIndicator =
    syncingStatus.step !== SyncSteps.waiting
      ? 'syncing'
      : isOfflineState
        ? 'networkerror'
        : notAuthorized
          ? 'autherror'
          : hasAssetErrors
            ? 'syncerror'
            : 'synced';

  const memoSyncIndicatorIcon = useMemo(
    () => <SyncIndicatorIcon type={typeForSyncIndicator} />,
    [typeForSyncIndicator],
  );

  if (!hasUserId) {
    return null;
  }

  return (
    <View
      style={[
        styles.bottomRightContainer,
        {
          bottom: ABSOLUTE_RIGHT_POSITION + (isAndroid ? bottom : 0),
          right: ABSOLUTE_RIGHT_POSITION + (isAndroid ? right : 0),
        },
      ]}
    >
      <DevIndicator />
      {showSyncIcon && (
        <View style={styles.syncContainer}>
          {hasItemsToSync && <View style={styles.changesBadge} />}
          <SyncIndicatorBody
            type={typeForSyncIndicator}
            SyncIcon={memoSyncIndicatorIcon}
            onPress={toggleSyncDialog}
          />
        </View>
      )}
    </View>
  );
};

const SyncIndicatorBody = ({
  onPress,
  type,
  // icon,
  SyncIcon,
}: {
  onPress: () => void;
  SyncIcon: React.ReactElement;
  type: 'syncing' | 'synced' | 'autherror' | 'syncerror' | 'networkerror';
  // icon: ICONS | null;
}): React.ReactElement => {
  const backgroundColor =
    type === 'networkerror'
      ? '#999999'
      : type === 'autherror' || type === 'syncerror'
        ? theme.colors.red
        : theme.colors.green;

  // const iconColor =
  //   type === 'networkerror' ? theme.colors.black : theme.colors.white;

  return (
    <Glass
      backgroundColor={backgroundColor}
      tintColor={backgroundColor}
      style={{ borderRadius: 99 }}
    >
      <Pressable
        onPress={onPress}
        style={[styles.container]}
        pressedStyle={{
          opacity: 0.5,
        }}
      >
        <ViewWithSeparators
          horizontal
          style={{ alignItems: 'center' }}
          separatorMargin={theme.paddingHorizontalHalf}
          separatorColor="transparent"
        >
          {SyncIcon}
        </ViewWithSeparators>
      </Pressable>
    </Glass>
  );
};

const SyncIndicatorIcon = ({
  type,
}: {
  type: 'syncing' | 'synced' | 'autherror' | 'syncerror' | 'networkerror';
}): React.ReactElement => {
  const rotationRef = useSharedValue(0);
  React.useEffect(() => {
    if (type === 'syncing') {
      rotationRef.value = withRepeat(
        withTiming(1, { duration: 1000, easing: Easing.linear }),
        0,
      );
    }
    return () => {
      if (type === 'syncing') {
        cancelAnimation(rotationRef);
        rotationRef.value = 0;
      }
    };
  }, [rotationRef, type]);

  const animatedStyles = useAnimatedStyle(() => {
    return {
      transform: [
        {
          rotate: `${interpolate(rotationRef.value, [0, 1], [0, 360])}deg`,
        },
      ],
    };
  });

  const iconSource =
    type === 'networkerror'
      ? ICONS.WifiSlashLight
      : type === 'autherror'
        ? ICONS.KeyLight
        : type === 'syncerror'
          ? ICONS.ExclamationSolid
          : ICONS.RotateDuotone;
  const iconColor =
    type === 'networkerror' ? theme.colors.black : theme.colors.white;

  return type !== 'syncing' ? (
    <Icon source={iconSource} size={ICON_SIZE} color={iconColor} />
  ) : (
    <Animated.View style={animatedStyles}>
      <Icon source={iconSource} size={ICON_SIZE} color={iconColor} />
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  changesBadge: {
    backgroundColor: theme.colors.red,
    borderColor: 'white',
    borderRadius: 7,
    borderWidth: 1,
    elevation: 6,
    height: 14,
    position: 'absolute',
    right: ABSOLUTE_RIGHT_POSITION,
    bottom: ABSOLUTE_RIGHT_POSITION,
    width: 14,
    zIndex: 1,
  },
  container: {
    minWidth: CIRCLE_SIZE,
    height: CIRCLE_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: CIRCLE_SIZE / 4,
  },
  bottomRightContainer: {
    zIndex: 9999,
    overflow: 'visible',
    position: 'absolute',
    flexDirection: 'column',
    alignItems: 'flex-end',
    pointerEvents: 'box-none',
    gap: theme.padding,
  },
  syncContainer: {
    paddingRight: ABSOLUTE_RIGHT_POSITION,
    paddingBottom: ABSOLUTE_RIGHT_POSITION,
    paddingLeft: 6,
    marginRight: -ABSOLUTE_RIGHT_POSITION,
    marginBottom: -ABSOLUTE_RIGHT_POSITION,
    pointerEvents: 'auto',
    position: 'relative',
  },
});
